import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { Route, Switch, useLocation, useParams } from "react-router-dom";

import TestSamples from "@/features/qa/components/TestSamples";

import DetailView from "../../../components/detailView/DetailView";
import AttachedDocumentPreview from "../../../components/table/settings/AttachedDocumentPreview";
import { useMainStore } from "../../../contexts/Store";
import QALinkedDocuments from "./QALinkedDocuments";
import TestDetailsHeader from "./TestDetailsHeader";
import TestFindings from "./TestFindings";
import TestProcedures from "./TestProcedures";

type Props = {
  isInternal?: boolean;
};

function TestDetails({ isInternal }: Props) {
  // Import MobX stores
  const mainStore = useMainStore();

  // State
  const [isCompleted, setIsCompleted] = useState(false);
  const [programName, setProgramName] = useState<string | null>(null);
  const [showCheckbox, setShowCheckbox] = useState(false);
  const [showCheckboxFromSlideMenu, setShowCheckboxFromSlideMenu] =
    useState(false);

  // Hooks
  // @ts-expect-error TS(2339) FIXME: Property 'record_version_id' does not exist on typ... Remove this comment to see the full error message
  const { record_version_id: recordVersionID } = useParams();
  const location = useLocation();

  // Variables
  const { workspaceID } = mainStore.context;
  const recordVersion =
    mainStore.recordVersions.list.find((rv) => rv.id == recordVersionID) ||
    mainStore.recordVersions.current;
  const isQATestRecordView =
    location.pathname ===
    `/workspaces/${workspaceID}/modules/qa-tests/${recordVersionID}/record-view`;
  const inCompleted =
    mainStore.avroSchemas?.firstValueForField("status", recordVersion?.data) ===
    "completed";
  const fieldTestName = mainStore.avroSchemas?.displayNameForField("test_name");
  const isFindings = location.pathname.includes("/findings");

  // Effects
  useEffect(() => {
    if (fieldTestName) {
      setProgramName(fieldTestName);
    }
  }, [fieldTestName]);

  useEffect(() => {
    if (isQATestRecordView) {
      setIsCompleted(inCompleted);
    }
  }, [inCompleted]);

  useEffect(() => {
    if (!location.pathname) {
      return;
    }

    setShowCheckbox(false);
  }, [location.pathname]);

  // functions
  // @ts-expect-error TS(7006) FIXME: Parameter 'targetField' implicitly has an 'any' ty... Remove this comment to see the full error message
  function maxDateFunction(targetField) {
    if (targetField == "start_date") {
      return mainStore.avroSchemas.valueForField(
        "target_completion_date",
        // @ts-expect-error TS(2533) FIXME: Object is possibly 'null' or 'undefined'.
        recordVersion.data,
      );
    }
    if (targetField == "period_start_date") {
      return mainStore.avroSchemas.valueForField(
        "period_end_date",
        // @ts-expect-error TS(2533) FIXME: Object is possibly 'null' or 'undefined'.
        recordVersion.data,
      );
    }
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'targetField' implicitly has an 'any' ty... Remove this comment to see the full error message
  function minDateFunction(targetField) {
    if (targetField === "target_completion_date") {
      return mainStore.avroSchemas.valueForField(
        "start_date",
        // @ts-expect-error TS(2533) FIXME: Object is possibly 'null' or 'undefined'.
        recordVersion.data,
      );
    }
    if (targetField == "period_end_date") {
      return mainStore.avroSchemas.valueForField(
        "period_start_date",
        // @ts-expect-error TS(2533) FIXME: Object is possibly 'null' or 'undefined'.
        recordVersion.data,
      );
    }
  }

  return (
    <div className="qa-test-details" data-testid="test-details">
      {!location.pathname.startsWith(
        `/workspaces/${workspaceID}/modules/qa-tests/${recordVersionID}/view-procedures/`,
      ) &&
        !location.pathname.startsWith(
          `/workspaces/${workspaceID}/modules/qa-tests/${recordVersionID}/add-new-procedures/`,
        ) &&
        !location.pathname.endsWith("/new-record") &&
        !location.pathname.startsWith(
          `/workspaces/${workspaceID}/modules/qa-tests/${recordVersionID}/view-findings/`,
        ) &&
        !location.pathname.startsWith(
          `/workspaces/${workspaceID}/modules/qa-tests/${recordVersionID}/add-new-findings/`,
        ) &&
        !location.pathname.startsWith(
          `/workspaces/${workspaceID}/modules/qa-tests/${recordVersionID}/samples/`,
        ) && (
          <TestDetailsHeader
            recordVersionID={recordVersionID}
            isInternal={isInternal}
            isFindings={isFindings}
            setShowCheckboxFromSlideMenu={setShowCheckboxFromSlideMenu}
          />
        )}
      <Switch>
        <Route
          exact
          path="/workspaces/:workspace_id/modules/qa-tests-development/:record_version_id"
        >
          <DetailView
            moduleIdentifier="qa_tests_development"
            disabledFields={["status"]}
            ignoredFields={["testers"]}
            minDateFunction={minDateFunction}
            maxDateFunction={maxDateFunction}
          />
        </Route>
        <Route
          exact
          path="/workspaces/:workspace_id/modules/qa-tests/:record_version_id/new-record"
        >
          <DetailView
            moduleIdentifier="qa_tests_development"
            disabledFields={["status"]}
            ignoredFields={["testers"]}
            minDateFunction={minDateFunction}
            maxDateFunction={maxDateFunction}
          />
        </Route>

        <Route
          exact
          path="/workspaces/:workspace_id/modules/qa-tests/:record_version_id/linked_documents"
        >
          <QALinkedDocuments isCompleted={isCompleted} />
        </Route>

        <Route
          exact
          path="/workspaces/:workspace_id/modules/qa-tests/:record_version_id/findings"
        >
          <TestFindings
            recordVersionId={Number(recordVersionID)}
            showCheckbox={showCheckbox}
            setShowCheckbox={setShowCheckbox}
            showCheckboxFromSlideMenu={showCheckboxFromSlideMenu}
          />
        </Route>

        <Route
          exact
          path="/workspaces/:workspace_id/modules/qa-tests/:record_version_id/procedures"
        >
          <TestProcedures
            recordVersionId={Number(recordVersionID)}
            isCompleted={isCompleted}
          />
        </Route>

        <Route
          exact
          path="/workspaces/:workspace_id/modules/qa-tests/:record_version_id/samples"
        >
          <TestSamples
            recordVersionId={Number(recordVersionID)}
            showCheckbox={showCheckbox}
            setShowCheckbox={setShowCheckbox}
          />
        </Route>

        <Route
          exact
          path="/workspaces/:workspace_id/modules/qa-tests/:record_version_id/add-new-procedures/:record_version_id"
        >
          <DetailView
            moduleIdentifier="qa_procedures"
            programName={programName}
          />
        </Route>
        <Route
          exact
          path="/workspaces/:workspace_id/modules/qa-tests/:record_version_id/view-procedures/:record_version_id"
        >
          <DetailView
            showCommentsButton
            moduleIdentifier="qa_procedures"
            programName={programName}
          />
        </Route>
        <Route
          exact
          path="/workspaces/:workspace_id/modules/qa-tests/:record_version_id/add-new-findings/:record_version_id"
        >
          <DetailView
            moduleIdentifier="qa_findings"
            ignoredFields={[]}
            minDateFunction={minDateFunction}
            maxDateFunction={maxDateFunction}
          />
        </Route>
        <Route
          exact
          path="/workspaces/:workspace_id/modules/qa-tests/:record_version_id/view-findings/:record_version_id"
        >
          <DetailView
            showCommentsButton
            moduleIdentifier="qa_findings"
            minDateFunction={minDateFunction}
            maxDateFunction={maxDateFunction}
          />
        </Route>
        <Route
          exact
          path="/workspaces/:workspace_id/modules/qa-tests/:record_version_id/samples/:record_version_id"
        >
          <DetailView showCommentsButton moduleIdentifier="samples" />
        </Route>
        <Route
          exact
          path="/workspaces/:workspace_id/modules/qa-tests/:record_version_id/preview/:attached_document_id"
        >
          <AttachedDocumentPreview />
        </Route>
      </Switch>
    </div>
  );
}

export default observer(TestDetails);
